import { subject } from "@casl/ability";
import { type CrudList, type PastaApiListParams, PastaCrudApi, useClassFactory } from "@pasta/box-v2";
import { CrudRequestBuilder } from "@pasta/crud-request";
import { type HomeDto } from "../dto/home.dto";
import {
  type LetterFileCloneDto,
  type LetterFileDto,
  type LetterFileSearchDto,
  type TransferLfDto,
  type UpdateLetterFileDto,
} from "../dto/letter-file.dto";
import { LinkPurpose } from "../dto/letter-file-document.dto";

export class LetterFileApi extends PastaCrudApi<{
  Entity: LetterFileDto;
  CreateDto: object;
  UpdateDto: UpdateLetterFileDto;
}> {
  protected readonly baseEndpoint = `letter-file/`;

  map<T extends LetterFileDto>(entity: T) {
    return subject("LetterFile", entity);
  }

  async create(): Promise<LetterFileDto> {
    const result = await this.network.post(
      `${this.baseEndpoint}`,
      {},
      {
        paramsSerializer: qb => qb.build(),
      }
    );
    return result.data;
  }

  async getByChrono(chronoNumber: string) {
    const result = await this.network.get(`${this.baseEndpoint}chrono/${chronoNumber}`);
    return this.map(result.data);
  }

  async fetchById(id: number, params?: { xVersion: string }): Promise<LetterFileDto> {
    let data: LetterFileDto | undefined;
    try {
      const result = await this.network.get(
        `${this.baseEndpoint}${id}`,
        {},
        {
          headers: params?.xVersion ? { "X-VERSION": params.xVersion } : undefined,
        }
      );
      if (result) {
        data = result.data;
      }
    } catch (e: any) {
      if (e.statusCode === 204) {
        return null as any;
      }
      throw e;
    }
    return data as LetterFileDto;
  }

  async getHome(): Promise<HomeDto> {
    const result = await this.network.get(`${this.baseEndpoint}home`);
    const homeResponse: HomeDto = result.data;
    homeResponse.todo.data = homeResponse.todo.data.map(lf => this.map(lf));
    homeResponse.current.data = homeResponse.current.data.map(lf => this.map(lf));
    homeResponse.done.data = homeResponse.done.data.map(lf => this.map(lf));
    return result.data;
  }

  async search(
    dto: LetterFileSearchDto,
    pagination: { offset: number; limit: number }
  ): Promise<CrudList<LetterFileDto>> {
    const result = await this.network.post(`${this.baseEndpoint}search`, dto, {
      paramsSerializer: qb => qb.build(),
      params: new CrudRequestBuilder().setOffset(pagination.offset).setLimit(pagination.limit),
    });
    return result.data;
  }

  async undraft(id: number) {
    const result = await this.network.post(`${this.baseEndpoint}${id}/undraft`);
    return this.map(result.data);
  }

  async next(id: number) {
    const result = await this.network.post(`${this.baseEndpoint}${id}/next`);
    return this.map(result.data);
  }

  async take(id: number) {
    const result = await this.network.post(`${this.baseEndpoint}${id}/take`);
    return this.map(result.data);
  }

  async appendDownstream(id: number, mail: string, position?: number) {
    await this.network.post(`${this.baseEndpoint}${id}/downstream`, {
      mail,
      position,
    });
  }

  async emptyDownstream(id: number) {
    await this.network.delete(`${this.baseEndpoint}${id}/downstream`);
  }

  async reorderDownstream(id: number, downstream: number[]) {
    await this.network.post(`${this.baseEndpoint}${id}/downstream/reorder`, {
      downstream,
    });
  }

  async removeDownstream(id: number, downstreamId: number) {
    await this.network.delete(`${this.baseEndpoint}${id}/downstream/${downstreamId}`);
  }

  async reorderFolders(id: number, folders: number[]) {
    await this.network.post(`${this.baseEndpoint}${id}/folders/reorder`, {
      folders,
    });
  }

  async removeAllDocs(id: number) {
    await this.network.delete(`${this.baseEndpoint}${id}/documents`);
  }

  async applyFlux(id: number, fluxId: number) {
    await this.network.post(`${this.baseEndpoint}${id}/apply-flux/${fluxId}`);
  }

  async generateArchive(id: number, purpose?: LinkPurpose): Promise<string> {
    const purposeQuery = purpose ? `purpose=${purpose}` : "";
    const result = await this.network.post(`${this.baseEndpoint}${id}/archive?${purposeQuery}`);
    return result.data;
  }

  async clone(id: number, body: LetterFileCloneDto): Promise<LetterFileDto> {
    const result = await this.network.post(`${this.baseEndpoint}${id}/clone`, body);
    return this.map(result.data);
  }

  async unarchive(id: number) {
    await this.network.post(`${this.baseEndpoint}${id}/unarchive`);
  }

  async countTransferCandidates(dto: TransferLfDto): Promise<number> {
    const result = await this.network.post(`${this.baseEndpoint}transfer/count`, dto);
    return result.data.count;
  }

  async transfer(dto: TransferLfDto): Promise<number> {
    const result = await this.network.post(`${this.baseEndpoint}transfer`, dto);
    return result.data.transferredCount;
  }

  async exportTransferCandidates(dto: TransferLfDto): Promise<string> {
    const result = await this.network.post(`${this.baseEndpoint}transfer/export`, dto);
    return result.data;
  }

  protected fetchListQb(params: PastaApiListParams): CrudRequestBuilder {
    return super.fetchListQb(params).addJoin("tags");
  }
}

export const [letterFileApiToken, useLetterFileApi] = useClassFactory(LetterFileApi);
